import { Grid, ListItem } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import AutocompleteGroupedList from "../../../common/components/functional/AutocompleteGroupedList/AutocompleteGroupedList";
import AddButton from "../../../common/components/functional/addButton/AddButton";
import AutocompleteBase from "../../../common/components/functional/fields/autocompleteBase/AutocompleteBase";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useMessage from "../../../hooks/useMessage";
import { fetchSkills } from "../../skills/skillsSlice";
import { Talent } from "../../talents/talentAPI";
import {
  selectTalentById,
  updateTalentSkills,
} from "../../talents/talentsSlice";
import { LocalizedSkill } from "../LocalizedGroups";

type Props = {
  onClose: () => void;
};
const TalentEditSkills = ({ onClose }: Props) => {
  const { talentId } = useParams();
  const talent = useAppSelector(
    (state) => selectTalentById(state, talentId as string) as Talent,
  );
  const existingSkillIds =
    talent?.skills?.values?.map((skill) => skill.id) || [];
  const [availableSkills, setAvailableSkills] = useState<LocalizedSkill[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<
    LocalizedSkill[] | null
  >();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const { sendMessage } = useMessage();
  const { i18n, t } = useTranslation(["common", "talentProfile"]);

  const talentWorkTitleIds = useMemo(
    () => talent?.workTitles?.values.map((wt) => wt.id).toString(),
    [talent?.workTitles?.values],
  );
  const localizedSkillIds = useAppSelector((state) => state.localized?.skill);
  const localizedFieldOfWorkIds = useAppSelector(
    (state) => state.localized?.fieldOfWork,
  );

  useEffect(() => {
    const language = i18n.language.substring(0, 2);
    // If talent has no work titles, no need to fetch skills
    if (!talentWorkTitleIds) return;
    dispatch(
      fetchSkills({
        language,
        workTitleIds: talentWorkTitleIds,
      }),
    )
      .unwrap()
      .then((skills) => {
        const sortedSkills = skills
          .map((skill) => ({
            ...skill,
            name: localizedSkillIds[skill.id],
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
          .sort((a, b) => a.fieldOfWorkId.localeCompare(b.fieldOfWorkId));

        setAvailableSkills(sortedSkills);
      });
  }, [
    i18n.language,
    localizedSkillIds,
    talentWorkTitleIds,
    dispatch,
    localizedFieldOfWorkIds,
  ]);

  const onSubmit = (localizedSkills: LocalizedSkill[]) => {
    const currentSkills = talent.skills || { values: [], version: 0 };
    const newSkills = localizedSkills.map((skill) => ({
      id: skill.id,
      fieldOfWorkId: skill.fieldOfWorkId,
    }));

    const updatedSkills = currentSkills.values
      ? [...currentSkills.values, ...newSkills]
      : newSkills;

    if (talent.id) {
      setIsLoading(true);
      dispatch(
        updateTalentSkills({
          talentId: talent.id,
          version: currentSkills.version,
          skills: updatedSkills,
        }),
      )
        .then(() => {
          sendMessage(t("talentProfile:skillsUpdated"), "success");
          onClose();
        })
        .catch(() => sendMessage(t("common:errorOccurred"), "error"))
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <FormSection title={t("talentProfile:addSkill")} margin noBorder>
      <OverlaySpinner isLoading={isLoading} />
      <Grid item container alignItems="center" justifyContent="center" xs={8}>
        <AutocompleteBase<LocalizedSkill>
          value={selectedSkills || []}
          options={availableSkills}
          getOptionLabel={(option) => option.name}
          getOptionDisabled={(option) => existingSkillIds.includes(option.id)}
          groupBy={(option) => localizedFieldOfWorkIds[option.fieldOfWorkId]}
          renderOption={(props, option) => (
            <ListItem {...props} key={option.id}>
              {option.name}
            </ListItem>
          )}
          renderGroup={({ group, children }) => (
            <AutocompleteGroupedList
              key={group}
              group={group}
              children={children}
            />
          )}
          placeholder={t("common:searchSkills")}
          onChange={(newValue) => setSelectedSkills(newValue)}
        />
      </Grid>
      <Grid
        item
        container
        xs={4}
        alignItems="center"
        justifyContent="flex-start"
      >
        <AddButton
          variant="contained"
          sx={{ marginLeft: "16px" }}
          label={t("common:save")}
          disabled={!selectedSkills?.length}
          onClick={() => {
            if (selectedSkills) {
              onSubmit(selectedSkills);
            }
          }}
        />
      </Grid>
    </FormSection>
  );
};

export default TalentEditSkills;
